import Rollbar from 'rollbar';
import env, { isProd } from './env';

const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
};

const BOT_USER_AGENT = /bot\b|crawler|spider|HeadlessChrome|Google-Ads/i;

// Not sent to Rollbar:
// - antd form validation rejections ({ values, errorFields }): the user's to
//   fix, and `values` is everything they typed (name, birth date, email...);
// - crawlers and headless browsers, whose blocked assets (e.g. "Unable to
//   preload CSS") are not something a real visitor hit.
export const shouldIgnoreReport = (args, userAgent) =>
  (args || []).some((arg) => Array.isArray(arg?.errorFields)) ||
  BOT_USER_AGENT.test(userAgent || '');

// The only Rollbar instance: App hands it to <RollbarProvider instance>.
// A second one used to live here and re-reported every uncaught error without
// ignoredMessages, IP anonymisation or the URL redaction below.
export const rollbar = new Rollbar({
  accessToken: env.ROLLBAR_TOKEN,
  environment: env.ROLLBAR_ENV,
  enabled: isProd,
  captureUncaught: isProd,
  captureUnhandledRejections: isProd,
  captureIp: 'anonymize',
  captureEmail: false,
  captureUsername: false,
  autoInstrument: isProd,
  captureConsole: isProd ? { levels: ['error', 'warning'] } : false,
  recorder: { enabled: false },
  ignoredMessages: [
    'Script error',
    'ResizeObserver loop',
    'Network request failed',
    'Failed to fetch',
    'Load failed',
  ],
  checkIgnore: (isUncaught, args) =>
    shouldIgnoreReport(args, globalThis.navigator?.userAgent),
  payload: {
    client: {
      javascript: {
        code_version: import.meta.env.VITE_GIT_SHA || __APP_VERSION__,
        source_map_enabled: true,
      },
    },
    context:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    platform: 'browser',
    framework: 'react',
    person: {
      id: readStorage('@contenta-user-id'),
      username: readStorage('@contenta-user-email'),
      email: readStorage('@contenta-user-email'),
    },
  },
  transform: (payload) => {
    if (payload.body && payload.body.message) {
      payload.body.message.extra = payload.body.message.extra || {};
      payload.body.message.extra.appVersion = __APP_VERSION__;
      payload.body.message.extra.time = new Date().toISOString();

      // Redact Zoom meeting credentials (password/signature/sessionKey) that
      // ride in the /meet URL query string before sending the URL to Rollbar.
      const url = new URL(window.location.href);
      ['password', 'signature', 'sessionKey'].forEach((param) => {
        if (url.searchParams.has(param)) {
          url.searchParams.set(param, '[REDACTED]');
        }
      });
      payload.body.message.extra.url = url.toString();
    }
  },
});

const errorLogger = ({ message, error, extraInfo }) => {
  if (error) {
    // Passing the error itself (not wrapped in custom data) gives Rollbar the
    // stack trace to group and source-map by.
    rollbar.error(message || 'Error logged', error, { extraInfo });
  } else if (message) {
    rollbar.info(message, { extraInfo });
  }
};

export default errorLogger;
